import { ArrowLeftOutlined, BarChartOutlined, EyeOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Form, Input, Popconfirm, Skeleton, Space, Spin, Tag, Tooltip, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import {
  helpPublishBlockers,
  HELP_ARTICLE_STATUS_LABELS,
  type HelpAnnounceOptions,
  type HelpArticleEntity,
  type HelpArticleKind,
  type HelpArticleStatus,
  type HelpChangeType,
  type HelpPlatform,
  type HelpRelease,
} from "@fitness-app/data-models/entities/HelpArticle";
import { EMPTY_HELP_CONTENT, parseHelpDraftContent, type HelpContent } from "@fitness-app/data-models/entities/HelpContent";

import { usePermission } from "~/hooks/usePermission";
import styles from "./editor/helpEditor.module.css";
import { HelpContentField } from "./editor/HelpContentField";
import {
  emptyHelpArticleForm,
  slugifyHelpTitle,
  toHelpArticleForm,
  toHelpArticleRow,
  type HelpArticleFormValue,
} from "./helpArticleForm";
import { HelpArticleSettings } from "./HelpArticleSettings";
import { HelpArticleStatsDrawer } from "./HelpArticleStatsDrawer";
import { helpCenterListPath } from "./helpCenterPaths";
import { PublishHelpArticleModal } from "./PublishHelpArticleModal";
import {
  useAnnounceHelpArticle,
  useChangeHelpArticleStatus,
  useCurrentUserId,
  useHelpArticles,
  useHelpPreviewLink,
  useSaveHelpArticle,
} from "./useHelpCenter";

const STATUS_COLORS: Record<HelpArticleStatus, string> = { draft: "gold", verified: "blue", published: "green" };

/**
 * Redakcja materiału na własnej stronie, nie w szufladzie: instrukcja bywa
 * długa, ma kroki, obrazy i film, a pisanie tego w wąskim panelu obok listy
 * było walką z miejscem. Tytuł, zajawka i treść są na stronie, cała reszta
 * metadanych chowa się w „Ustawieniach”.
 */
const HelpArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm<HelpArticleFormValue>();
  const values = Form.useWatch([], form);

  const articlesQuery = useHelpArticles();
  const saveArticle = useSaveHelpArticle();
  const changeStatus = useChangeHelpArticleStatus();
  const announceArticle = useAnnounceHelpArticle();
  const previewLink = useHelpPreviewLink();
  const userId = useCurrentUserId();
  const canPublish = usePermission(PermissionKey.HELP_CENTER_PUBLISH, PermissionScope.ALL);

  const isNew = !id || id === "nowy";
  const article = useMemo(
    () => (isNew ? null : ((articlesQuery.data ?? []).find((item) => item.id === id) ?? null)),
    [articlesQuery.data, id, isNew],
  );
  const kind = (article?.kind as HelpArticleKind) ?? ((searchParams.get("kind") as HelpArticleKind) || "guide");

  const [content, setContent] = useState<HelpContent>(EMPTY_HELP_CONTENT);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  // Który materiał siedzi w stanie — a nie „czy cokolwiek już wczytano”.
  // Trasa `/:id` nie przemontowuje tej strony przy przejściu między
  // materiałami, więc zwykła flaga zostawała włączona i formularz dalej
  // pokazywał poprzedni wpis.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  // Publikacja to zapis ORAZ zmiana statusu, więc `changeStatus.isLoading` sam
  // nie wystarcza — przez czas zapisu przycisk stałby bezczynnie i kolejne
  // kliknięcie zapisywałoby materiał drugi raz.
  const [publishing, setPublishing] = useState(false);
  // Materiał zapisany tuż przed publikacją czeka tu na decyzję o ogłoszeniu.
  // Trzymamy WYNIK zapisu, nie `article` z listy: okno pyta o wpis w wersji,
  // która właśnie poleciała do bazy.
  const [toPublish, setToPublish] = useState<HelpArticleEntity | null>(null);
  // Ogłoszenie materiału, który już jest opublikowany — tu chodzi wyłącznie
  // o popup, nie o wypuszczenie treści.
  const [toAnnounce, setToAnnounce] = useState<HelpArticleEntity | null>(null);
  const target = isNew ? "nowy" : (id ?? "");
  const ready = loadedFor === target;

  useEffect(() => {
    if (ready || (!isNew && !article)) {
      return;
    }

    form.setFieldsValue(article ? toHelpArticleForm(article) : emptyHelpArticleForm(kind));
    const parsed = article ? parseHelpDraftContent(article.content) : [];
    setContent(parsed.length ? parsed : EMPTY_HELP_CONTENT);
    setLoadedFor(target);
  }, [article, form, isNew, kind, ready, target]);

  const relatedOptions = useMemo(
    () => (articlesQuery.data ?? []).map((item) => ({ value: item.slug, label: `${item.title} (${item.slug})` })),
    [articlesQuery.data],
  );

  // Pusty akapit to nie jest treść — materiał z samym tytułem nie ma czego
  // pokazać klientce.
  const contentForPublication = content.filter(
    (block) => !(block.type === "p" && block.children.every((child) => "text" in child && !child.text.trim())),
  );
  const blockers = values
    ? helpPublishBlockers({
        kind,
        platforms: values.platforms as HelpPlatform[],
        changeType: values.changeType as HelpChangeType | null,
        releasedAt: values.releasedAt ? values.releasedAt.format("YYYY-MM-DD") : null,
        releases: values.releases as HelpRelease[] | null,
        content: contentForPublication,
      })
    : [];

  const save = async () => {
    try {
      await form.validateFields();
    } catch {
      setSettingsOpen(true);
      void message.warning("Uzupełnij ustawienia materiału.");
      return null;
    }

    // `getFieldsValue(true)` zamiast wyniku walidacji: pola nierenderowane dla
    // tego rodzaju materiału nie wracają z `validateFields`, a mapowanie musi
    // dostać komplet.
    const saved = await saveArticle.mutateAsync({
      id: article?.id ?? null,
      row: toHelpArticleRow(form.getFieldsValue(true), { userId, isNew: !article, content }),
    });

    if (isNew && saved?.id) {
      navigate(`/help-center/${saved.id}`, { replace: true });
    }

    return saved;
  };

  if (!isNew && articlesQuery.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.column}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!isNew && !article) {
    return (
      <div className={styles.page}>
        <div className={styles.column}>
          <Typography.Title level={4}>Nie ma takiego materiału</Typography.Title>
          <Button onClick={() => navigate(helpCenterListPath(kind))}>Wróć do listy</Button>
        </div>
      </div>
    );
  }

  const status = (article?.status as HelpArticleStatus) ?? "draft";

  return (
    <Form form={form} layout="vertical" initialValues={emptyHelpArticleForm(kind)} className={styles.page}>
      <div className={styles.column}>
        <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 24 }} wrap>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(helpCenterListPath(kind))}>
            Centrum pomocy
          </Button>
          <Space size={4} wrap>
            <Tag color={STATUS_COLORS[status]}>{HELP_ARTICLE_STATUS_LABELS[status]}</Tag>
            {article && (
              <Tooltip title={status === "published" ? "Podgląd publiczny" : "Link z podglądem szkicu, ważny dobę"}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  loading={previewLink.isLoading}
                  onClick={() =>
                    previewLink.mutate(article.id, { onSuccess: (url) => window.open(url, "_blank", "noopener") })
                  }
                />
              </Tooltip>
            )}
            {article?.publishedAt && (
              <Tooltip title="Statystyki">
                <Button type="text" icon={<BarChartOutlined />} onClick={() => setStatsOpen(true)} />
              </Tooltip>
            )}
            <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
              Ustawienia
            </Button>
            <Button type="primary" loading={saveArticle.isLoading} onClick={() => void save()}>
              Zapisz
            </Button>
            {/* Przy samym `helpCenter.manage` przycisku po prostu nie ma —
                wyszarzony bez wyjaśnienia byłby zagadką. */}
            {canPublish &&
              article &&
              (status === "published" ? (
                <>
                  {article.kind === "update" && (
                    <Button onClick={() => setToAnnounce(article)}>
                      {article.announcedAt ? "Ogłoś ponownie" : "Ogłoś klientkom"}
                    </Button>
                  )}
                  <Popconfirm
                    title="Cofnąć publikację?"
                    description="Materiał i jego media przestaną być dostępne dla klientek od razu."
                    okText="Cofnij"
                    cancelText="Zostaw"
                    okButtonProps={{ loading: changeStatus.isLoading }}
                    onConfirm={() => changeStatus.mutate({ id: article.id, status: "verified", article })}
                  >
                    <Button loading={changeStatus.isLoading}>Cofnij publikację</Button>
                  </Popconfirm>
                </>
              ) : (
                <Tooltip title={blockers.length ? blockers.join(" ") : ""}>
                  <Button
                    disabled={blockers.length > 0}
                    loading={publishing}
                    onClick={async () => {
                      // Publikujemy dopiero po zapisie: inaczej klientka
                      // zobaczyłaby wersję sprzed ostatnich poprawek.
                      setPublishing(true);
                      // `save()` zwraca `null` tylko przy błędzie walidacji —
                      // odrzucenie zapisu (sieć, RLS, trigger) leci dalej i bez
                      // `catch` zostawiłoby przycisk zakręcony do przeładowania
                      // strony, czyli bez możliwości ponowienia.
                      let saved: Awaited<ReturnType<typeof save>> = null;
                      try {
                        saved = await save();
                      } catch {
                        // Komunikat pokazuje już mutacja zapisu.
                      }
                      if (!saved) {
                        setPublishing(false);
                        return;
                      }
                      // Wpis „Co nowego” pyta jeszcze o ogłoszenie; instrukcja
                      // publikuje się od razu, bo nie ma czego ogłaszać.
                      if (saved.kind === "update") {
                        setToPublish(saved);
                        setPublishing(false);
                        return;
                      }

                      changeStatus.mutate(
                        { id: saved.id, status: "published", article: saved },
                        { onSettled: () => setPublishing(false) },
                      );
                    }}
                  >
                    Publikuj
                  </Button>
                </Tooltip>
              ))}
          </Space>
        </Space>

        <Form.Item name="title" rules={[{ required: true, message: "Tytuł jest wymagany." }]} className={styles.title}>
          <Input.TextArea
            autoSize
            variant="borderless"
            placeholder="Bez tytułu"
            onBlur={(event) => {
              if (!article && !form.getFieldValue("slug")) {
                form.setFieldValue("slug", slugifyHelpTitle(event.target.value));
              }
            }}
          />
        </Form.Item>

        <Form.Item
          name="summary"
          rules={[{ required: true, message: "Zajawka jest wymagana." }]}
          className={styles.summary}
        >
          <Input.TextArea autoSize variant="borderless" placeholder="Krótka zajawka — co klientka z tego ma." />
        </Form.Item>

        {/*
          Edytor dostaje wartość początkową RAZ, przy montowaniu — Plate nie
          dosyła jej później. Wpuszczony przed wczytaniem materiału stawał się
          pusty i tak już zostawał, a jego `onChange` nadpisywał stan pustką,
          którą „Zapisz” zapisywał na opublikowanym materiale. Przy pierwszym
          otwarciu ratował nas `React.lazy` — chunk ładował się wolniej niż
          efekt — ale przy drugim materiale w tej samej sesji chunk jest już
          w pamięci i edytor montuje się synchronicznie.

          Ta sama bramka załatwia przejście między materiałami: `ready` wisi na
          tożsamości materiału, więc przy zmianie adresu gaśnie na jeden render
          i edytor montuje się od nowa z właściwą treścią. `key` byłby tu
          martwym kodem.
        */}
        {ready ? (
          <HelpContentField value={content} onChange={setContent} />
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
      </div>

      <PublishHelpArticleModal
        article={toPublish}
        open={!!toPublish}
        loading={changeStatus.isLoading}
        onCancel={() => setToPublish(null)}
        onConfirm={(announce: HelpAnnounceOptions | null) => {
          if (!toPublish) {
            return;
          }

          changeStatus.mutate(
            { id: toPublish.id, status: "published", article: toPublish, announce },
            { onSettled: () => setToPublish(null) },
          );
        }}
      />

      <PublishHelpArticleModal
        article={toAnnounce}
        open={!!toAnnounce}
        mode="announce"
        loading={announceArticle.isLoading}
        onCancel={() => setToAnnounce(null)}
        onConfirm={(announce) => {
          if (!toAnnounce || !announce) {
            setToAnnounce(null);
            return;
          }

          announceArticle.mutate({ article: toAnnounce, announce }, { onSettled: () => setToAnnounce(null) });
        }}
      />

      {article && <HelpArticleStatsDrawer article={article} open={statsOpen} onClose={() => setStatsOpen(false)} />}

      <HelpArticleSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        kind={kind}
        slugLocked={status === "published"}
        relatedOptions={relatedOptions}
        blockers={blockers}
      />
    </Form>
  );
};

export default HelpArticlePage;
